/**
 * Site-wide accessibility guardrails for Webflow-authored/runtime DOM.
 *
 * The Designer remains the source of truth. These small runtime protections
 * cover dynamic content and component instances that are easy to miss in a
 * static audit: hidden focus targets, unnamed icon links, and untitled embeds.
 */

const SOCIAL_LABELS: Array<[string, string]> = [
  ["instagram.com", "Billion Oyster Project on Instagram"],
  ["facebook.com", "Billion Oyster Project on Facebook"],
  ["youtube.com", "Billion Oyster Project on YouTube"],
  ["linkedin.com", "Billion Oyster Project on LinkedIn"],
];

function repairKnownLink(link: HTMLAnchorElement) {
  if (link.getAttribute("href") !== "#") return;
  const text = link.textContent?.replace(/\s+/g, " ").trim();

  if (text === "membership@billionoysterproject.org") {
    link.href = "mailto:membership@billionoysterproject.org";
  } else if (text === "Explore our membership") {
    link.href = "/donate#membership-card";
  }
}

function labelLink(link: HTMLAnchorElement) {
  repairKnownLink(link);
  if (link.hasAttribute("aria-label")) return;
  const visibleName = link.textContent?.trim()
    || link.querySelector<HTMLImageElement>("img[alt]")?.alt.trim();
  if (visibleName) return;

  if (link.matches(".v3nav__brand")) {
    link.setAttribute("aria-label", "Billion Oyster Project home");
    return;
  }
  if (link.matches(".overlay-link") && /\/innovation\/?$/.test(link.pathname)) {
    link.setAttribute("aria-label", "Explore innovation at Billion Oyster Project");
    return;
  }
  if (link.matches(".member__social")) {
    const member = link.closest(".team-item, [data-team-member]");
    const memberName = member?.querySelector<HTMLElement>(
      ".member__name, [data-member-name], h2, h3, h4",
    )?.textContent?.trim();
    if (memberName) {
      const network = SOCIAL_LABELS.find(([host]) => link.href.includes(host))?.[1]
        .replace("Billion Oyster Project on ", "") || "social profile";
      link.setAttribute("aria-label", `${memberName} on ${network}`);
      return;
    }
  }
  const social = SOCIAL_LABELS.find(([host]) => link.href.includes(host));
  if (social) link.setAttribute("aria-label", social[1]);
}

const FIELD_LABELS: Record<string, string> = {
  email: "Email address",
  "email-2": "Email address",
  "contact-name": "Name",
  "contact-email": "Email address",
  "contact-msg": "Message",
  zip: "ZIP code",
  password: "Password",
};

function hasProgrammaticLabel(field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) {
  if (field.getAttribute("aria-label")?.trim() || field.getAttribute("aria-labelledby")?.trim()) {
    return true;
  }
  return Boolean(field.id && document.querySelector(`label[for="${CSS.escape(field.id)}"]`));
}

function labelField(field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) {
  if (field instanceof HTMLInputElement && field.type === "hidden") return;
  if (hasProgrammaticLabel(field)) return;

  const label = FIELD_LABELS[field.id]
    || (field instanceof HTMLInputElement && field.type === "email" ? "Email address" : "");
  if (label) field.setAttribute("aria-label", label);
}

function titleFrame(frame: HTMLIFrameElement) {
  if (frame.title.trim()) return;
  if (/youtube|vimeo/i.test(frame.src)) frame.title = "Embedded video";
  else frame.title = "Embedded content";
}

function syncHiddenState(el: HTMLElement) {
  if (el.getAttribute("aria-hidden") === "true") el.setAttribute("inert", "");
  else if (el.getAttribute("aria-hidden") === "false") el.removeAttribute("inert");
}

function enhance(root: ParentNode) {
  if (root instanceof HTMLAnchorElement) labelLink(root);
  if (root instanceof HTMLIFrameElement) titleFrame(root);
  if (
    root instanceof HTMLInputElement ||
    root instanceof HTMLSelectElement ||
    root instanceof HTMLTextAreaElement
  ) {
    labelField(root);
  }
  if (root instanceof HTMLElement && root.hasAttribute("aria-hidden")) {
    syncHiddenState(root);
  }

  root.querySelectorAll?.<HTMLAnchorElement>("a[href]").forEach(labelLink);
  root.querySelectorAll?.<HTMLIFrameElement>("iframe").forEach(titleFrame);
  root.querySelectorAll?.<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    "input, select, textarea",
  ).forEach(labelField);
  root.querySelectorAll?.<HTMLElement>("[aria-hidden]").forEach(syncHiddenState);
}

function init() {
  enhance(document);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "attributes") {
        syncHiddenState(mutation.target as HTMLElement);
      }
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) enhance(node);
      });
    });
  }).observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["aria-hidden"],
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}

export {};
